import { createServerFn } from "@tanstack/react-start"
import { countBy } from "lodash-es"
import { z } from "zod"

import {
  reviewKind,
  reviewKinds,
} from "@/admin/identity-reviews/review-presentation"
import { IdentityReviewRequestSchema } from "@/terminal/company-identity/identity-review-request"

// Every endpoint here is directly callable, so each handler asserts the admin
// itself. An admin login is the authority to record an Identity Decision from
// the console (owner ruling 2026-10-04); the admin's email is its actor.
const adminActor = async () => {
  const { assertAdminActor } = await import("@/lib/supabase/auth")
  return assertAdminActor()
}

// The Companies each open material review keeps from Monitoring: the ones it
// names, their canonical Companies, and every successor an established
// Acquisition Carryover reaches, as `loadBlockingReviewIds` reads it.
const blockedCompaniesByReviewId = async (
  reviews: { identityReviewId: string; companyIds: string[] }[]
) => {
  const [
    { inArray },
    { terminalCompany },
    { db },
    { affectedCompaniesByRoot },
  ] = await Promise.all([
    import("drizzle-orm"),
    import("@/db/schema/terminal-company-identity"),
    import("@/lib/admin-db"),
    import("@/terminal/company-identity/identity-changes"),
  ])
  const rootIds = [...new Set(reviews.flatMap(({ companyIds }) => companyIds))]
  const companyIdsByRoot = await db.transaction((tx) =>
    affectedCompaniesByRoot(tx, rootIds)
  )
  const blockedIds = [...new Set([...companyIdsByRoot.values()].flat())]
  const names = blockedIds.length
    ? await db
        .select({ id: terminalCompany.id, name: terminalCompany.display_name })
        .from(terminalCompany)
        .where(inArray(terminalCompany.id, blockedIds))
    : []
  const nameById = new Map(names.map(({ id, name }) => [id, name]))
  return new Map(
    reviews.map(({ identityReviewId, companyIds }) => [
      identityReviewId,
      [
        ...new Set(
          companyIds.flatMap((id) => companyIdsByRoot.get(id) ?? [id])
        ),
      ].map((companyId) => ({
        companyId,
        displayName: nameById.get(companyId) ?? null,
      })),
    ])
  )
}

export const blockingFilters = ["all", "blocking", "other"] as const
// How many Companies' reviews the queue sends at a time. Open reviews run to
// the tens of thousands, too many to send and render at once.
export const queueCompanyPageSize = 40

// The Company a queue row is filed under, and its docket: a Company's
// blocking and non-blocking reviews are separate dockets.
export const queueCompanyKey = ({
  companies,
  identityReviewId,
}: {
  companies: { companyId: string }[]
  identityReviewId: string
}) => companies[0]?.companyId ?? identityReviewId
const queueDocketKey = (
  review: Parameters<typeof queueCompanyKey>[0] & { isMaterial: boolean }
) => `${review.isMaterial}:${queueCompanyKey(review)}`

// The queue's working order, shared by the queue and a review's previous and
// next links: reviews that block Monitoring first, then one Company's reviews
// together so its Company Scope stays in mind, newest first within it.
const compareQueueOrder = (left: QueueOrderKey, right: QueueOrderKey) =>
  Number(right.isMaterial) - Number(left.isMaterial) ||
  queueCompanyName(left).localeCompare(queueCompanyName(right)) ||
  String(right.createdAt).localeCompare(String(left.createdAt))

type QueueOrderKey = {
  isMaterial: boolean
  createdAt: string | Date
  companies: { displayName: string | null }[]
}

const queueCompanyName = ({ companies }: QueueOrderKey) =>
  companies[0]?.displayName ?? ""

// Every open review's id in queue order, read from only the fields the order
// compares, so a review finds its place without the queue's evidence.
const readOpenQueueOrder = async () => {
  const [
    { desc, eq, isNull, sql },
    { terminalCompany, terminalIdentityReview },
    { db },
  ] = await Promise.all([
    import("drizzle-orm"),
    import("@/db/schema/terminal-company-identity"),
    import("@/lib/admin-db"),
  ])
  const rows = await db
    .select({
      identityReviewId: terminalIdentityReview.id,
      isMaterial: terminalIdentityReview.is_material,
      createdAt: terminalIdentityReview.created_at,
      firstCompanyName: terminalCompany.display_name,
    })
    .from(terminalIdentityReview)
    .leftJoin(
      terminalCompany,
      eq(
        terminalCompany.id,
        sql`${terminalIdentityReview.affected_company_ids}[1]`
      )
    )
    .where(isNull(terminalIdentityReview.resolved_at))
    .orderBy(desc(terminalIdentityReview.created_at))
  return rows
    .map(({ firstCompanyName, ...row }) => ({
      ...row,
      companies: [{ displayName: firstCompanyName }],
    }))
    .sort(compareQueueOrder)
    .map(({ identityReviewId }) => identityReviewId)
}

// The party each review's Source Records name, and where it was recorded, so
// a queue row says what the review is about rather than its generic reason.
const recordedPartiesByReviewId = async (identityReviewIds: string[]) => {
  if (!identityReviewIds.length) return new Map<string, RecordedParty[]>()
  const [
    { eq, inArray, sql },
    { terminalIdentityReviewSourceRecord },
    { terminalSource, terminalSourceRecord },
    { db },
  ] = await Promise.all([
    import("drizzle-orm"),
    import("@/db/schema/terminal-company-identity"),
    import("@/db/schema/terminal-research"),
    import("@/lib/admin-db"),
  ])
  const parties = await db
    .selectDistinct({
      identityReviewId: terminalIdentityReviewSourceRecord.identity_review_id,
      name: sql<
        string | null
      >`${terminalSourceRecord.recorded_subject}->>'legal_name'`,
      platformKey: terminalSource.platform_key,
      recordKind: terminalSourceRecord.record_kind,
    })
    .from(terminalIdentityReviewSourceRecord)
    .innerJoin(
      terminalSourceRecord,
      eq(
        terminalSourceRecord.id,
        terminalIdentityReviewSourceRecord.source_record_id
      )
    )
    .innerJoin(
      terminalSource,
      eq(terminalSource.id, terminalSourceRecord.source_id)
    )
    .where(
      inArray(
        terminalIdentityReviewSourceRecord.identity_review_id,
        identityReviewIds
      )
    )
  const partiesByReviewId = new Map<string, RecordedParty[]>()
  for (const { identityReviewId, ...party } of parties)
    partiesByReviewId.set(identityReviewId, [
      ...(partiesByReviewId.get(identityReviewId) ?? []),
      party,
    ])
  return partiesByReviewId
}

type RecordedParty = {
  name: string | null
  platformKey: string
  recordKind: string
}

const loadOpenQueue = async () => {
  const { listOpenIdentityReviews } =
    await import("@/terminal/company-identity/identity-review-operations")
  const reviews = await listOpenIdentityReviews()
  const [blockedByReviewId, partiesByReviewId] = await Promise.all([
    blockedCompaniesByReviewId(
      reviews
        .filter(({ isMaterial }) => isMaterial)
        .map(({ identityReviewId, companies }) => ({
          identityReviewId,
          companyIds: companies.map(({ companyId }) => companyId),
        }))
    ),
    recordedPartiesByReviewId(
      reviews.map(({ identityReviewId }) => identityReviewId)
    ),
  ])
  return reviews
    .map((review) => ({
      ...review,
      blockedCompanies: blockedByReviewId.get(review.identityReviewId) ?? [],
      recordedParties: partiesByReviewId.get(review.identityReviewId) ?? [],
    }))
    .sort(compareQueueOrder)
}

// The filtered queue's first dockets, with counts over every open review. The
// filters run here, over the whole queue, so the page receives only the
// dockets it shows.
export const loadIdentityReviewQueue = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        blocking: z.enum(blockingFilters),
        kind: z.enum(["all", ...reviewKinds]),
        query: z.string(),
        companyLimit: z.number().int().positive(),
      })
      .strict()
  )
  .handler(async ({ data: { blocking, kind, query, companyLimit } }) => {
    await adminActor()
    const reviews = await loadOpenQueue()
    const needle = query.trim().toLowerCase()
    const matches = reviews
      .filter(
        ({ isMaterial }) =>
          blocking === "all" || isMaterial === (blocking === "blocking")
      )
      .filter((review) => kind === "all" || reviewKind(review) === kind)
      .filter(
        (review) =>
          !needle ||
          [
            review.reason,
            review.identityReviewId,
            ...review.recordedParties.map(({ name }) => name ?? ""),
            ...review.companies.flatMap(({ companyId, displayName }) => [
              companyId,
              displayName ?? "",
            ]),
          ].some((text) => text.toLowerCase().includes(needle))
      )
    // The queue order keeps each docket's reviews together.
    const docketKeys = [...new Set(matches.map(queueDocketKey))]
    const shownKeys = new Set(docketKeys.slice(0, companyLimit))
    const firstBlocking = reviews.find(({ isMaterial }) => isMaterial)
    return {
      reviews: matches.filter((review) =>
        shownKeys.has(queueDocketKey(review))
      ),
      matchedDocketCount: docketKeys.length,
      openCount: reviews.length,
      blockingCount: reviews.filter(({ isMaterial }) => isMaterial).length,
      companyCount: new Set(reviews.map(queueCompanyKey)).size,
      countByKind: countBy(reviews, reviewKind),
      startId: (firstBlocking ?? matches[0] ?? reviews[0])?.identityReviewId,
      isStartBlocking: firstBlocking !== undefined,
      refreshedAt: new Date().toISOString(),
    }
  })

export type IdentityReviewQueue = Awaited<
  ReturnType<typeof loadIdentityReviewQueue>
>

export type IdentityReviewQueueRow = IdentityReviewQueue["reviews"][number]

export const countOpenIdentityReviews = createServerFn({
  method: "GET",
}).handler(async () => {
  const [{ count, isNull, sql }, { terminalIdentityReview }, { db }] =
    await Promise.all([
      import("drizzle-orm"),
      import("@/db/schema/terminal-company-identity"),
      import("@/lib/admin-db"),
    ])
  await adminActor()
  const [counts] = await db
    .select({
      openCount: count(),
      blockingCount:
        sql<number>`count(*) filter (where ${terminalIdentityReview.is_material})`.mapWith(
          Number
        ),
    })
    .from(terminalIdentityReview)
    .where(isNull(terminalIdentityReview.resolved_at))
  return counts
})

// What a person weighs when two Companies may be one business: the website
// and location intake was given, the Portfolios that hold each, and when it
// last ran.
const companyProfiles = async (companyIds: string[]) => {
  const [
    { and, eq, inArray, max },
    { db },
    { portfolio },
    { terminalCompany },
    { isCurrentPortfolioEntry, terminalPortfolioEntry },
    { terminalCompanyRun, terminalMonitoringRun },
  ] = await Promise.all([
    import("drizzle-orm"),
    import("@/lib/admin-db"),
    import("@/db/schema/public-terminal"),
    import("@/db/schema/terminal-company-identity"),
    import("@/db/schema/terminal-portfolio"),
    import("@/db/schema/terminal-schema"),
  ])
  const [companies, placements, runs] = await Promise.all([
    db
      .select({ id: terminalCompany.id, intake: terminalCompany.intake })
      .from(terminalCompany)
      .where(inArray(terminalCompany.id, companyIds)),
    db
      .selectDistinct({
        companyId: terminalPortfolioEntry.company_id,
        name: portfolio.name,
      })
      .from(terminalPortfolioEntry)
      .innerJoin(
        portfolio,
        eq(portfolio.id, terminalPortfolioEntry.portfolio_id)
      )
      .where(
        and(
          inArray(terminalPortfolioEntry.company_id, companyIds),
          isCurrentPortfolioEntry
        )
      )
      .orderBy(portfolio.name),
    db
      .select({
        companyId: terminalCompanyRun.company_id,
        lastRunAt: max(terminalMonitoringRun.started_at),
      })
      .from(terminalCompanyRun)
      .innerJoin(
        terminalMonitoringRun,
        eq(terminalMonitoringRun.id, terminalCompanyRun.monitoring_run_id)
      )
      .where(inArray(terminalCompanyRun.company_id, companyIds))
      .groupBy(terminalCompanyRun.company_id),
  ])
  return Object.fromEntries(
    companies.map(({ id, intake }) => [
      id,
      {
        website: intake?.website ?? null,
        locations: [
          ...new Set(
            (intake?.location_leads ?? [])
              .map(({ city, subdivision_code }) =>
                [city, subdivision_code?.replace(/^US-/, "")]
                  .filter(Boolean)
                  .join(", ")
              )
              .filter(Boolean)
          ),
        ],
        portfolios: placements
          .filter(({ companyId }) => companyId === id)
          .map(({ name }) => name),
        lastRunAt:
          runs.find(({ companyId }) => companyId === id)?.lastRunAt ?? null,
      },
    ])
  )
}

export const loadIdentityReview = createServerFn({ method: "GET" })
  .inputValidator(z.object({ identityReviewId: z.string().uuid() }).strict())
  .handler(async ({ data }) => {
    const [
      { showIdentityReview },
      { identityReviewOperationRefusal, verdictAcceptance },
    ] = await Promise.all([
      import("@/terminal/company-identity/identity-review-operations"),
      import("@/terminal/company-identity/identity-review-refusal"),
    ])
    await adminActor()
    const [review, openIds] = await Promise.all([
      showIdentityReview(data),
      readOpenQueueOrder(),
    ])
    const profileByCompanyId = await companyProfiles(
      review.companies.map(({ companyId }) => companyId)
    )
    // Where this review sits in the open queue, and the reviews either side,
    // so a person steps through the queue and lands on the next one after
    // resolving. A resolved review is out of the queue: its next is the first.
    const index = openIds.indexOf(review.identityReviewId)
    const queue = {
      position: index === -1 ? null : index + 1,
      openCount: openIds.length,
      previousId: index > 0 ? openIds[index - 1] : null,
      nextId: (index === -1 ? openIds[0] : openIds[index + 1]) ?? null,
    }
    const blockedCompanies =
      index !== -1 && review.isMaterial
        ? (
            await blockedCompaniesByReviewId([
              {
                identityReviewId: review.identityReviewId,
                companyIds: review.companies.map(({ companyId }) => companyId),
              },
            ])
          ).get(review.identityReviewId)!
        : []
    const operations = ["accept", "discard", "confirm", "reject"] as const
    return {
      review,
      queue,
      blockedCompanies,
      profileByCompanyId,
      acceptance: verdictAcceptance(review),
      refusalByOperation: Object.fromEntries(
        operations.map((operation) => [
          operation,
          identityReviewOperationRefusal(review, operation),
        ])
      ) as Record<(typeof operations)[number], string | null>,
    }
  })

export type IdentityReviewDetail = Awaited<
  ReturnType<typeof loadIdentityReview>
>

export const recordIdentityReviewDecision = createServerFn({ method: "POST" })
  .inputValidator(
    z
      .object({
        identityReviewId: z.string().uuid(),
        request: IdentityReviewRequestSchema,
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const { decideIdentityReview } =
      await import("@/terminal/company-identity/identity-review-decision")
    const actor = await adminActor()
    return decideIdentityReview({ ...data, actor })
  })

// Human Brand confirmation, with the Company's own website or none (CONTEXT:
// Company intake). It closes a shared intake review once its last
// unconfirmed Company is confirmed.
export const confirmReviewedCompanyWebsite = createServerFn({ method: "POST" })
  .inputValidator(
    z
      .object({
        companyId: z.string().uuid(),
        website: z.string(),
        brandName: z.string().optional(),
        effectiveFrom: z.string().optional(),
        isStartBounded: z.boolean().optional(),
        rationale: z.string().optional(),
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const { confirmCompanyWebsite } =
      await import("@/terminal/company-identity/confirm-company-website")
    const actor = await adminActor()
    return confirmCompanyWebsite({ ...data, actor })
  })

// The CLI's `separate`: opens a review over one exact Mention and its cluster
// and records the person's separation against it at once.
export const separateIndividualMention = createServerFn({ method: "POST" })
  .inputValidator(
    z
      .object({
        individualMentionId: z.string().uuid(),
        rationale: z.string(),
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const [{ openIndividualSeparationReview }, { decideIdentityReview }] =
      await Promise.all([
        import("@/terminal/company-identity/open-individual-review"),
        import("@/terminal/company-identity/identity-review-decision"),
      ])
    const actor = await adminActor()
    const { identityReviewId } = await openIndividualSeparationReview(
      data.individualMentionId
    )
    await decideIdentityReview({
      identityReviewId,
      request: { operation: "reject", rationale: data.rationale },
      actor,
    })
    return { identityReviewId }
  })
